// tresciUi.ts — pomocniki ekranów treści aplikacji współdzielone przez komponenty.
import { useEffect, useState } from 'react';
import { opisLokalny, utcNaWarszawe } from './czasWarszawa';
import { tresciApi, type PozycjaKatalogu, type UczenKatalogu } from '@/lib/api/tresci';
import { ApiError, describeApiError } from '@/lib/api/errors';
import { useServices } from '@/lib/ServicesContext';

/** Czas UTC z serwera pokazany operatorowi w Europe/Warsaw. */
export function wWarszawie(utc: string | null | undefined): string {
  if (!utc) return '—';
  return opisLokalny(utcNaWarszawe(new Date(utc)));
}

export interface WartoscOdbiorcow {
  miasta: PozycjaKatalogu[];
  szkoly: PozycjaKatalogu[];
  uczniowie: UczenKatalogu[];
}

export const PUSCI_ODBIORCY: WartoscOdbiorcow = { miasta: [], szkoly: [], uczniowie: [] };

/**
 * Powód odmowy dla operatora. Serwer CC odsyła `error` (+ `szczegoly`: komunikat systemu głównego,
 * kolizje 409, czego brakuje w konfiguracji) — operator ma zobaczyć TO, nie „HTTP 503 POST …".
 */
export function komunikatBledu(e: unknown): string {
  if (e instanceof ApiError) {
    const cialo = (e.body ?? {}) as {
      error?: unknown;
      szczegoly?: { komunikat?: unknown; kolizje?: unknown; czego_brak?: unknown };
    };
    if (typeof cialo.error === 'string' && cialo.error) {
      const s = cialo.szczegoly ?? {};
      const czesci = [cialo.error];
      if (typeof s.komunikat === 'string' && s.komunikat) czesci.push(s.komunikat);
      if (Array.isArray(s.kolizje) && s.kolizje.length) {
        const nazwy = s.kolizje.map((k) => {
          const p = (k ?? {}) as { id?: unknown; name?: unknown };
          return `${typeof p.name === 'string' ? p.name : 'pozycja'} #${String(p.id ?? '?')}`;
        });
        czesci.push(`Koliduje z: ${nazwy.join(', ')}.`);
      }
      if (Array.isArray(s.czego_brak) && s.czego_brak.length) czesci.push(`Brakuje: ${s.czego_brak.join(', ')}.`);
      return czesci.join(' ');
    }
    return describeApiError(e);
  }
  return e instanceof Error ? e.message : 'Nieoczekiwany błąd aplikacji.';
}

/**
 * Wynik zapisu NIEZNANY (recenzja R5-15 10.10): zerwane połączenie, przekroczony czas albo 5xx (poza 503
 * „zapis niepodłączony"). Operacja MOGŁA się wykonać — ekran odświeża listę i mówi to wprost, zamiast
 * zostawić wrażenie odmowy (ponowne kliknięcie utworzyłoby wtedy duplikat). Odmowa (4xx) to co innego:
 * lista zostaje bez zmian (B8).
 */
export function wynikNieznany(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  if (e.kind === 'network') return true;
  return e.status !== null && e.status !== undefined && e.status >= 500 && e.status !== 503;
}

/** Dopisek do komunikatu przy wyniku nieznanym — jedno brzmienie na wszystkich ekranach treści. */
export const DOPISEK_WYNIK_NIEZNANY =
  ' Wynik NIEZNANY — lista została odświeżona: sprawdź ją, zanim spróbujesz ponownie.';

/** Serwer CC nie ma drogi zapisu treści (brak adresu systemu głównego albo klucza maszynowego). */
export const POWOD_BRAKU_ZAPISU =
  'Zapis treści aplikacji nie jest w tym środowisku podłączony — przyciski zapisu są wyłączone.';

export interface DostepZapisu {
  /** `true` tylko, gdy serwer CC potwierdził, że droga zapisu jest podłączona. */
  dostepny: boolean;
  /** Dlaczego zapis jest niedostępny — pokazywane przy wyłączonych przyciskach. */
  powod: string | null;
}

/**
 * Czy w TYM środowisku zapis treści jest podłączony — pyta serwer CC (`/stan`), nie zgaduje.
 * Do odpowiedzi przyciski zapisu są wyłączone; 503 przy zapisie zostaje drugą linią obrony.
 */
export function useDostepZapisuTresci(): DostepZapisu {
  const { api } = useServices();
  const [stan, setStan] = useState<{ podlaczony: boolean; czegoBrak: string[] } | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    let aktualne = true;
    tresciApi.stan(api)
      .then((s) => { if (aktualne) setStan({ podlaczony: s?.zapis_podlaczony === true, czegoBrak: s?.czego_brak ?? [] }); })
      .catch((e: unknown) => { if (aktualne) setBlad(komunikatBledu(e)); });
    return () => { aktualne = false; };
  }, [api]);

  if (stan?.podlaczony) return { dostepny: true, powod: null };
  if (stan) {
    return {
      dostepny: false,
      powod: POWOD_BRAKU_ZAPISU + (stan.czegoBrak.length ? ` Brakuje: ${stan.czegoBrak.join(', ')}.` : ''),
    };
  }
  if (blad) return { dostepny: false, powod: `Nie udało się ustalić, czy zapis treści jest podłączony: ${blad}` };
  return { dostepny: false, powod: 'Sprawdzam, czy zapis treści jest w tym środowisku podłączony…' };
}
